import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "./PageHeader.scss";

export type PageHeaderProps = {
  title: React.ReactNode;
  /** A line under the title */
  subtitle?: React.ReactNode;
  /** A number after the title, e.g. the row count of a list page */
  count?: number;
  /** A small label above the title (a parent's name: the tournament of a category page…) */
  eyebrow?: React.ReactNode;
  /** A back arrow: a route, or a handler */
  back?: { to?: string; onClick?: () => void; label?: string };
  /** The page's buttons, right-aligned */
  actions?: React.ReactNode;
  /** Under the title row: tabs, a filter bar… */
  children?: React.ReactNode;
  className?: string;
};

/** The top of every page: display-font title with the orange slash, optional count, subtitle and actions. */
export function PageHeader({ title, subtitle, count, eyebrow, back, actions, children, className }: PageHeaderProps): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <header className={["ui-page-header", className].filter(Boolean).join(" ")}>
      <div className="ui-page-header__row">
        {back ? (
          <button
            type="button"
            className="ui-page-header__back"
            aria-label={back.label ?? "Back"}
            data-tooltip={back.label ?? "Back"}
            onClick={() => (back.onClick ? back.onClick() : back.to ? navigate(back.to) : navigate(-1))}
          >
            <ArrowLeft size={18} />
          </button>
        ) : null}
        <div className="ui-page-header__titles">
          {eyebrow ? <div className="ui-page-header__eyebrow">{eyebrow}</div> : null}
          <h1 className="ui-page-header__title">
            <span>{title}</span>
            {count !== undefined ? <span className="ui-page-header__count">{count}</span> : null}
          </h1>
          {subtitle ? <p className="ui-page-header__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="ui-page-header__actions">{actions}</div> : null}
      </div>
      {children ? <div className="ui-page-header__extra">{children}</div> : null}
    </header>
  );
}

export default PageHeader;
